import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, Package } from 'lucide-react';

const Holdings = ({ holdings, totalMarketValue, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Investment Holdings
        </h3>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Loading holdings...
        </div>
      </div>
    );
  }

  if (!holdings || holdings.length === 0) {
    return null; // Don't show holdings section if no holdings
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  const formatQuantity = (value) => {
    // Show up to 6 decimal places for mutual funds, remove trailing zeros
    return parseFloat((value || 0).toFixed(6)).toString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Investment Holdings
            </h3>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Portfolio Value</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(totalMarketValue)}
            </p>
          </div>
        </div>
      </div>

      {/* Holdings List */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Symbol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Avg Cost
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Market Value
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Gain/Loss
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Return %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {holdings.map((holding) => {
              const isGain = holding.unrealized_gain_loss >= 0;
              const gainLossColor = isGain
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400';

              return (
                <tr
                  key={holding.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {holding.ticker}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {holding.security_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {holding.security_type}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatQuantity(holding.quantity)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatCurrency(holding.average_cost)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(holding.current_value)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`flex items-center justify-end space-x-1 text-sm font-medium ${gainLossColor}`}>
                      {isGain ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      <span>{formatCurrency(Math.abs(holding.unrealized_gain_loss))}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm font-medium ${gainLossColor}`}>
                      {isGain ? '+' : ''}{formatPercent(holding.unrealized_gain_loss_percent)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {holdings.length} {holdings.length === 1 ? 'holding' : 'holdings'}
          </span>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Gain/Loss</p>
              <p className={`text-sm font-semibold ${
                holdings.reduce((sum, h) => sum + (h.unrealized_gain_loss || 0), 0) >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(holdings.reduce((sum, h) => sum + (h.unrealized_gain_loss || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Holdings;
